import { EditorStarterAsset } from '../../assets/assets';
import {
  getAssetDurationInSeconds,
  getAssetStartInSeconds,
} from '../../assets/utils';
import { getItemPlaybackRate } from '../get-item-playback-rate';
import { EditorStarterItem } from '../item-type';
import { calculateFrozenFrames } from '../../timeline/timeline-transition-item/calculate-frozen-frames';

/**
 * 트랜지션 확장 계산에 필요한 파라미터
 */
export interface TransitionExtensionParams {
  sourceItem: EditorStarterItem;
  targetItem: EditorStarterItem;
  assets: Record<string, EditorStarterAsset>;
  fps: number;
  transitionFrom: number;
  transitionDuration: number;
  /** 성능 최적화: 미리 계산된 source 확장 가능 프레임 */
  cachedSourceExtendable?: number;
  /** 성능 최적화: 미리 계산된 target 확장 가능 프레임 */
  cachedTargetExtendable?: number;
}

/**
 * 트랜지션 확장 계산 결과
 */
export interface TransitionExtensionResult {
  sourceExtendedFrames: number;
  targetExtendedFrames: number;
  targetExtendedAssetStartDelta: number;
  sourceFrozenFrames: number;
  targetFrozenFrames: number;
}

/**
 * Target 아이템이 앞으로 확장 가능한 프레임 수를 계산합니다.
 * assetStart가 0보다 크면 앞에 재생 가능한 영상이 있습니다.
 */
export const getTargetExtendableFrames = ({
  targetItem,
  fps,
}: {
  targetItem: EditorStarterItem;
  fps: number;
}): number => {
  const assetStart = getAssetStartInSeconds(targetItem);
  if (assetStart === null || assetStart <= 0) {
    return 0;
  }

  const playbackRate = getItemPlaybackRate(targetItem);
  return Math.floor((assetStart * fps) / playbackRate);
};

/**
 * Source 아이템이 뒤로 확장 가능한 프레임 수를 계산합니다.
 * 에셋의 총 길이에서 현재 사용 중인 부분을 빼면 확장 가능한 길이가 나옵니다.
 */
export const getSourceExtendableFrames = ({
  sourceItem,
  assets,
  fps,
}: {
  sourceItem: EditorStarterItem;
  assets: Record<string, EditorStarterAsset>;
  fps: number;
}): number => {
  // GIF/WebP는 자동 루프되므로 항상 확장 가능
  if (sourceItem.type === 'gif' || sourceItem.type === 'webp') {
    return Infinity;
  }

  if (!('assetId' in sourceItem) || !sourceItem.assetId) {
    return 0;
  }

  const asset = assets[sourceItem.assetId];
  if (!asset) {
    return 0;
  }

  const assetDuration = getAssetDurationInSeconds(asset);
  const assetStart = getAssetStartInSeconds(sourceItem) ?? 0;
  const playbackRate = getItemPlaybackRate(sourceItem);

  if (assetDuration === null) {
    return 0;
  }

  // 에셋에서 사용 가능한 총 프레임 수
  const totalAssetFrames = Math.floor(
    ((assetDuration - assetStart) * fps) / playbackRate,
  );

  // 현재 아이템이 사용 중인 프레임 수
  const currentUsedFrames = sourceItem.durationInFrames;

  return Math.max(0, totalAssetFrames - currentUsedFrames);
};

/**
 * Target 아이템의 확장 정보를 계산합니다.
 * 실제 아이템을 수정하지 않고, 확장에 필요한 assetStart 델타 값을 반환합니다.
 */
export const calculateTargetExtensionInfo = ({
  targetItem,
  framesToExtend,
  fps,
}: {
  targetItem: EditorStarterItem;
  framesToExtend: number;
  fps: number;
}): { assetStartDelta: number } => {
  if (framesToExtend <= 0) {
    return { assetStartDelta: 0 };
  }

  const playbackRate = getItemPlaybackRate(targetItem);
  // assetStart를 얼마나 앞으로 이동해야 하는지 (음수 값)
  const assetStartDelta = -((framesToExtend * playbackRate) / fps);

  return { assetStartDelta };
};

/**
 * 트랜지션 from 계산 유틸 (floor/ceil 일관성 유지)
 * boundaryFrame에서 duration의 절반을 뺀 위치를 반환합니다.
 */
export const calculateTransitionFrom = (
  boundaryFrame: number,
  durationInFrames: number,
): number => {
  return boundaryFrame - Math.floor(durationInFrames / 2);
};

/**
 * 트랜지션의 확장 정보와 frozen frames를 통합 계산합니다.
 *
 * 이 함수는 다음 곳에서 중복되던 로직을 통합합니다:
 * - add-transition.ts
 * - on-transition-extend-handler.ts
 * - sync-transitions-for-item.ts
 * - transition-inspector.tsx
 *
 * @param params 계산에 필요한 파라미터
 * @returns 확장 정보와 frozen frames
 */
export const calculateTransitionExtension = ({
  sourceItem,
  targetItem,
  assets,
  fps,
  transitionFrom,
  transitionDuration,
  cachedSourceExtendable,
  cachedTargetExtendable,
}: TransitionExtensionParams): TransitionExtensionResult => {
  const transitionEnd = transitionFrom + transitionDuration;

  // 트랜지션이 아이템 경계를 넘어가는 양 계산
  const sourceItemEnd = sourceItem.from + sourceItem.durationInFrames;
  const targetItemStart = targetItem.from;

  // source가 뒤로 확장해야 하는 프레임 수
  const sourceNeededExtension = Math.max(0, transitionEnd - sourceItemEnd);
  // target이 앞으로 확장해야 하는 프레임 수
  const targetNeededExtension = Math.max(0, targetItemStart - transitionFrom);

  // 실제로 확장 가능한 프레임 수 (캐시 값이 있으면 사용)
  const sourceExtendable =
    cachedSourceExtendable ??
    getSourceExtendableFrames({ sourceItem, assets, fps });
  const targetExtendable =
    cachedTargetExtendable ?? getTargetExtendableFrames({ targetItem, fps });

  // 실제로 확장할 프레임 수 (필요한 양과 가능한 양 중 작은 값)
  const sourceExtendedFrames = Math.min(
    sourceNeededExtension,
    sourceExtendable,
  );
  const targetExtendedFrames = Math.min(
    targetNeededExtension,
    targetExtendable,
  );

  // target 아이템의 assetStart 델타 계산
  const { assetStartDelta: targetExtendedAssetStartDelta } =
    calculateTargetExtensionInfo({
      targetItem,
      framesToExtend: targetExtendedFrames,
      fps,
    });

  // frozen frames 계산 (확장 정보 포함)
  const { sourceFrozenFrames, targetFrozenFrames } = calculateFrozenFrames({
    transition: {
      id: '',
      type: 'transition',
      transitionType: 'fade',
      durationInFrames: transitionDuration,
      sourceItemId: sourceItem.id,
      targetItemId: targetItem.id,
      trackId: '',
      from: transitionFrom,
    },
    sourceItem,
    targetItem,
    assets,
    fps,
    sourceExtendedFrames,
    targetExtendedFrames,
  });

  return {
    sourceExtendedFrames,
    targetExtendedFrames,
    targetExtendedAssetStartDelta,
    sourceFrozenFrames,
    targetFrozenFrames,
  };
};
